import { useState, useRef, useEffect } from "react";
import { X, Search, Loader2 } from "lucide-react";
import api from "../api/api"; // Your project's custom Axios instance path
import Toast, { useToast } from "../components/Toast";

/* ---------------- SECTIONS ---------------- */
const allSections = [
  "About",
  "Point Of Contacts",
];

const requiredSections = ["About"];

export default function AddAccountModal({ onClose, onSaveSuccess }) {
  const { toast, showToast, hideToast } = useToast();
  const [showRequired, setShowRequired] = useState(false);
  const [activeSection, setActiveSection] = useState("About");
  const [loading, setLoading] = useState(false);

  // Synchronized state initialization to track individual form input nodes perfectly
  const [form, setForm] = useState({
    cust_name: "",
    cust_address: "",
    cust_city: "",
    cust_state: "",
    cust_pincode: "",
    cust_cin: "",
    cust_gst: "",
    contacts: [
      {
        name: "",
        email: "",
        contact: "",
        designation: "",
      },
    ],
  });

  const sectionRefs = useRef({});
  const visibleSections = showRequired ? requiredSections : allSections;

  const isVisible = (section) =>
    !showRequired || requiredSections.includes(section);

  const scrollTo = (section) => {
    sectionRefs.current[section]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  /* ---------------- SCROLL SPY ---------------- */
  useEffect(() => {
    const handleScroll = () => {
      for (let sec of visibleSections) {
        const el = sectionRefs.current[sec];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(sec);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleSections]);

  useEffect(() => {
    if (showRequired) scrollTo("About");
  }, [showRequired]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /* ---------------- CONTACT HANDLERS ---------------- */
  const addContact = () => {
    updateField("contacts", [
      ...form.contacts,
      {
        name: "",
        email: "",
        contact: "",
        designation: "",
      },
    ]);
  };

  const updateContact = (index, field, value) => {
    const copy = [...form.contacts];
    copy[index] = { ...copy[index], [field]: value };
    updateField("contacts", copy);
  };

  const removeContact = (index) => {
    const copy = form.contacts.filter((_, i) => i !== index);
    updateField("contacts", copy);
  };

  /* ---------------- API INTEGRATION SUBMISSION ---------------- */
  const handleSubmit = async () => {
    if (!form.cust_name.trim()) {
      showToast("Customer Name is required.", "warning");
      return;
    }

    if (!form.cust_address.trim()) {
      showToast("Address is required.", "warning");
      scrollTo("About");
      return;
    }

    if (!form.cust_city.trim()) {
      showToast("City is required.", "warning");
      scrollTo("About");
      return;
    }

    if (!form.cust_state.trim()) {
      showToast("State is required.", "warning");
      scrollTo("About");
      return;
    }

    if (!form.cust_pincode.trim()) {
      showToast("Pincode is required.", "warning");
      scrollTo("About");
      return;
    }

    try {
      setLoading(true);

      // Grab current identity parameters safely
      const loggedInUserId = window.localStorage.getItem("userId") || null; 

      // Deep clean the contacts array so we don't pass half-empty tracking objects to backend queries
      const cleanedContacts = form.contacts.filter(contact => contact.name.trim() !== "");

      const payload = {
        cust_name: form.cust_name.trim(),
        cust_address: form.cust_address.trim(),
        cust_city: form.cust_city.trim(),
        cust_state: form.cust_state.trim(),
        cust_pincode: form.cust_pincode.trim(),
        cust_cin: form.cust_cin.trim() || null,
        cust_gst: form.cust_gst.trim() || null,
        
        // Dynamically tracking user properties
        cust_account_manager_id: loggedInUserId ? parseInt(loggedInUserId, 10) : null, 
        cust_last_updated_by: loggedInUserId ? parseInt(loggedInUserId, 10) : null,    
        
        cust_admin_approval: 1, 
        contacts: cleanedContacts,       
      };

      const response = await api.post("/customer/add", payload);

      if (response.data.success) {
        const successMessage = response.data.message || "Customer created successfully!";
        showToast(successMessage, "success");
        setTimeout(() => {
          if (onSaveSuccess) onSaveSuccess();
          onClose();
        }, 1200);
      }
    } catch (error) {
      console.error("Detailed Submission Error Log:", error);
      
      // Dig out what your backend is explicitly screaming about
      const serverErrorMessage = error.response?.data?.message;
      const statusText = error.response?.statusText;
      const statusCode = error.response?.status;

      if (statusCode === 401) {
        showToast("Your login session has expired. Please refresh your browser or log back in.", "error");
      } else if (serverErrorMessage) {
        showToast(`Server Error (${statusCode}): ${serverErrorMessage}`, "error");
      } else {
        showToast(`Network Error: Unable to complete operation. (Status: ${statusCode || "No Connection"})`, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <Toast toast={toast} onClose={hideToast} />
      {/* BACKDROP */}
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose()} />

      {/* DRAWER CONTAINER */}
      <div className="relative bg-white w-full max-w-6xl h-full flex flex-col md:flex-row ml-auto shadow-xl overflow-hidden">
        
        {/* SIDEBAR NAVIGATION */}
        <div className="w-full md:w-[220px] shrink-0 border-b md:border-b-0 md:border-r p-2 md:p-4 flex md:flex-col overflow-x-auto md:overflow-visible bg-slate-50/50">
          <div className="flex md:flex-col gap-1.5 md:gap-3 md:space-y-3">
            {visibleSections.map((sec) => (
              <div
                key={sec}
                onClick={() => scrollTo(sec)}
                className={`cursor-pointer whitespace-nowrap text-sm px-2.5 py-1.5 border-l-2 md:border-l-2 border-b-2 md:border-b-0 rounded md:rounded-none transition-all ${
                  activeSection === sec
                    ? "text-blue-600 border-blue-600 font-semibold bg-blue-50/50"
                    : "text-gray-500 border-transparent hover:text-gray-800"
                }`}
              >
                {sec}
              </div>
            ))}
          </div>
        </div>

        {/* INTERACTION SECTION */}
        <div className="flex-1 flex flex-col min-h-0">
          
          {/* HEADER ROW */}
          <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b sticky top-0 bg-white z-10">
            <h2 className="text-blue-600 font-semibold text-base sm:text-lg">Add Customer</h2>
            <button onClick={() => onClose()} className="p-1 rounded-full hover:bg-slate-100 transition">
              <X size={20} />
            </button>
          </div>

          {/* DYNAMIC SCROLL CONTAINER */}
          <div className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-10">
            
            {/* ABOUT MANAGEMENT CONFIG */}
            {isVisible("About") && (
              <section ref={(el) => (sectionRefs.current["About"] = el)} className="space-y-4">
                <h3 className="font-semibold text-base text-slate-800 border-b pb-2">About</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_name}
                      onChange={(e) => updateField("cust_name", e.target.value)}
                      placeholder="Corporate Name"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Address *</label>
                    <textarea
                      placeholder="Street, Office address details"
                      rows={2}
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_address}
                      onChange={(e) => updateField("cust_address", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">City *</label>
                    <input
                      type="text"
                      placeholder="City"
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_city}
                      onChange={(e) => updateField("cust_city", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">State *</label>
                    <input
                      type="text"
                      placeholder="State"
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_state}
                      onChange={(e) => updateField("cust_state", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pincode *</label>
                    <input
                      type="number"
                      placeholder="Pincode"
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_pincode}
                      onChange={(e) => updateField("cust_pincode", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">CIN</label>
                    <input
                      type="text"
                      placeholder="CIN Number"
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_cin}
                      onChange={(e) => updateField("cust_cin", e.target.value)}
                    />
                  </div>

                  <div className="col-span-2 lg:col-span-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">GST</label>
                    <input
                      type="text"
                      placeholder="GSTIN Identification Number"
                      className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={form.cust_gst}
                      onChange={(e) => updateField("cust_gst", e.target.value)}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* POINT OF CONTACTS (POC) SUBSYSTEM */}
            {isVisible("Point Of Contacts") && (
              <section ref={(el) => (sectionRefs.current["Point Of Contacts"] = el)} className="space-y-5">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-base font-semibold text-slate-900">Point Of Contacts</h3>
                  <button
                    type="button"
                    onClick={addContact}
                    className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition"
                  >
                    + Add Contact
                  </button>
                </div>

                <div className="space-y-4">
                  {form.contacts.map((contact, index) => (
                    <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-slate-700 text-sm">Contact {index + 1}</h4>
                        {form.contacts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContact(index)}
                            className="text-red-600 hover:text-red-700 font-medium text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Name</label>
                          <input
                            type="text"
                            placeholder="Enter Name"
                            value={contact.name}
                            onChange={(e) => updateContact(index, "name", e.target.value)}
                            className="w-full border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Email</label>
                          <input
                            type="email"
                            placeholder="Enter Email"
                            value={contact.email}
                            onChange={(e) => updateContact(index, "email", e.target.value)}
                            className="w-full border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Contact</label>
                          <input
                            type="number"
                            placeholder="Enter Contact"
                            value={contact.contact}
                            onChange={(e) => updateContact(index, "contact", e.target.value)}
                            className="w-full border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Designation</label>
                          <input
                            type="text"
                            placeholder="Enter Designation"
                            value={contact.designation}
                            onChange={(e) => updateContact(index, "designation", e.target.value)}
                            className="w-full border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* ACTION SUBMIT FOOTER */}
          <div className="border-t p-4 flex justify-end gap-3 sticky bottom-0 bg-white z-10 shadow-[0_-2px_10px_rgba(0,0,0,0.02)]">
            <button 
              onClick={() => onClose()} 
              disabled={loading}
              className="px-4 py-2 border rounded text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              className="px-5 py-2 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700 active:scale-[0.99] transition disabled:opacity-75 flex items-center gap-2"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Saving Customer...
                </>
              ) : (
                "Save Customer"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}